import { Transition } from '@headlessui/react';
import cn from 'clsx';
import { DragEventHandler, FC, MouseEventHandler, ReactNode, TouchEventHandler, useEffect, useState } from 'react';
import { useMeasure } from 'react-use';

import s from './Basic.module.scss';

const MIN_DRAWER_HEIGHT = 300;
const MIN_BACKGROUND_HEIGHT = 100;

export interface BottomDrawerProps {
  isStretchable?: boolean;
  children: ReactNode;
  isVisible: boolean;
  isFull?: boolean;
  onCloseDrawer: () => void;
  backDropClose?: boolean;
  className?: string;
}

const Basic: FC<React.PropsWithChildren<React.PropsWithChildren<BottomDrawerProps>>> = ({
  isStretchable = false,
  children = null,
  isVisible,
  isFull = false,
  onCloseDrawer,
  backDropClose = true,
  className
}) => {
  const [drawerHeight, setHeight] = useState<number>();
  const [backgroundRef, { height: backgroundHeight }] = useMeasure<HTMLDivElement>();

  // 드로워 닫으면 높이값 초기화
  useEffect(() => {
    if (!isVisible) {
      setHeight(undefined);
    }
  }, [isVisible]);

  const setDrawerHeight = (handlePosY: number) => {
    if (!!drawerHeight && drawerHeight < MIN_DRAWER_HEIGHT) {
      setHeight(300);
    } else if (!!drawerHeight && backgroundHeight - drawerHeight < MIN_BACKGROUND_HEIGHT) {
      setHeight(backgroundHeight - MIN_BACKGROUND_HEIGHT);
    } else if (handlePosY > 0) {
      setHeight(backgroundHeight - handlePosY);
    }
  };

  const handleTouchMove: TouchEventHandler<HTMLDivElement> = (e) => {
    const handlePosY = e.touches[0]?.clientY ?? 0;
    setDrawerHeight(handlePosY);
  };

  const handleDragStart: DragEventHandler<HTMLDivElement> = (e) => {
    const img = new Image();
    e.dataTransfer.setDragImage(img, 0, 0);
  };

  const handleDrag: DragEventHandler<HTMLDivElement> = (e) => {
    const handlePosY = e.clientY;
    setDrawerHeight(handlePosY);
  };

  const handleClickBackDrop: MouseEventHandler<HTMLDivElement> = () => {
    if (!backDropClose) return;
    onCloseDrawer();
  };

  // 바텀드로워 팝업시 바닥 스크롤 방지
  useEffect(() => {
    if (!isVisible) return () => {};

    const { documentElement } = document;
    const { overflow } = documentElement.style;

    documentElement.style.overflow = 'hidden';

    return () => {
      document.documentElement.style.overflow = overflow;
    };
  }, [isVisible]);

  return (
    <div className={cn(s.drawerPosition, { [s.full as string]: isFull })}>
      <Transition
        show={isVisible}
        enter="transition-opacity ease-linear duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className={cn(s.drawerBackground, { [s.full as string]: isFull })} ref={backgroundRef} onClick={handleClickBackDrop} />
      </Transition>
      <Transition
        show={isVisible}
        enter="transition ease-in-out duration-300 transform"
        enterFrom="translate-y-full"
        leave="transition ease-in-out duration-300 transform"
        leaveTo="translate-y-full"
      >
        <div className={cn(s.drawer_content_position, { [s.full as string]: isFull })}>
          <div
            className={cn(s.drawer, { [s.full as string]: isFull, [s.stretchable_drawer as string]: isStretchable }, className)}
            style={{ height: `${drawerHeight}px` }}
          >
            {/* topArea */}
            <div className={cn(s.top, { [s.stretchable as string]: isStretchable })}>
              {isStretchable && <div className={cn(s.handle)} draggable onTouchMove={handleTouchMove} onDragStart={handleDragStart} onDrag={handleDrag} />}
            </div>
            {children}
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default Basic;
